import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Sidebar } from '@/components/common/Sidebar';
import { DashboardHeader } from '@/components/dashboard';
import { 
  Bell, 
  Check, 
  Clock, 
  AlertCircle, 
  CheckCheck,
  Trash2,
  ChevronLeft,
  ArrowRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { notificationsService, type Notification } from '@/services/notifications';
import { formatDistanceToNow, format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export default function NotificationsPage() {
  const navigate = useNavigate();
  const { user, isLoading, logout } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'unread' | 'read'>('all');

  const fetchNotifications = async () => {
    try {
      const data = await notificationsService.getAll();
      setNotifications(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erro ao buscar notificações:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const filteredNotifications = (Array.isArray(notifications) ? notifications : []).filter(n => {
    if (filter === 'unread') return !n.isRead;
    if (filter === 'read') return n.isRead;
    return true;
  });

  const markAsRead = async (id: string) => {
    try {
      await notificationsService.markAsRead(id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, isRead: true } : n));
    } catch (error) {
      console.error('Erro ao marcar notificação como lida:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await notificationsService.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    } catch (error) {
      console.error('Erro ao marcar todas as notificações como lidas:', error);
    }
  };

  const deleteNotification = async (id: string) => {
    try {
      await notificationsService.delete(id);
      setNotifications(prev => prev.filter(n => n.id !== id));
    } catch (error) {
      console.error('Erro ao deletar notificação:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  if (isLoading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-neutral-950">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Carregando...</p>
        </div>
      </div>
    );
  }

  const getIcon = (type: Notification['type']) => {
    switch (type) {
      case 'warning': return <AlertCircle className="w-5 h-5" />;
      case 'success': return <Check className="w-5 h-5" />;
      case 'error': return <AlertCircle className="w-5 h-5" />;
      default: return <Clock className="w-5 h-5" />;
    }
  };

  const getTypeStyles = (type: Notification['type']) => {
    switch (type) {
      case 'warning': return "bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400";
      case 'success': return "bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400";
      case 'error': return "bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400";
      default: return "bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400";
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-neutral-950 flex">
      <Sidebar
        userRole={user.role}
        userName={user.name}
        onLogout={handleLogout}
      />

      <div className="flex-1 flex flex-col h-screen overflow-hidden">
        <DashboardHeader userName={user.name} />

        <main className="flex-1 overflow-auto p-8">
          <div className="max-w-4xl mx-auto space-y-6">
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center gap-4">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={() => navigate(-1)}
                  className="rounded-full"
                >
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Central de Notificações</h1>
                  <p className="text-sm text-gray-500">Gerencie todos os seus alertas e avisos do sistema</p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={markAllAsRead}
                  className="text-xs flex items-center gap-2"
                >
                  <CheckCheck className="w-4 h-4" /> Marcar todas como lidas
                </Button>
              </div>
            </div>

            {/* Filters */}
            <div className="flex items-center gap-4 border-b border-gray-200 dark:border-neutral-800 pb-1">
              <button 
                onClick={() => setFilter('all')}
                className={cn(
                  "px-4 py-2 text-sm font-medium border-b-2 transition-all",
                  filter === 'all' 
                    ? "border-teal-500 text-teal-600 dark:text-teal-400" 
                    : "border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                )}
              >
                Todas
              </button>
              <button 
                onClick={() => setFilter('unread')}
                className={cn(
                  "px-4 py-2 text-sm font-medium border-b-2 transition-all",
                  filter === 'unread' 
                    ? "border-teal-500 text-teal-600 dark:text-teal-400" 
                    : "border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                )}
              >
                Não lidas
              </button>
              <button 
                onClick={() => setFilter('read')}
                className={cn(
                  "px-4 py-2 text-sm font-medium border-b-2 transition-all",
                  filter === 'read' 
                    ? "border-teal-500 text-teal-600 dark:text-teal-400" 
                    : "border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                )}
              >
                Lidas
              </button>
            </div>

            {/* Notifications List */}
            <div className="space-y-4 pt-4">
              {loading ? (
                <div className="flex flex-col items-center justify-center py-20">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-500 mb-4" />
                  <p className="text-gray-500">Carregando notificações...</p>
                </div>
              ) : filteredNotifications.length > 0 ? (
                filteredNotifications.map((notification) => (
                  <div 
                    key={notification.id}
                    className={cn(
                      "bg-white dark:bg-neutral-900 rounded-xl border p-5 transition-all flex gap-5 group",
                      !notification.isRead 
                        ? "border-teal-200 dark:border-teal-900/50 shadow-sm shadow-teal-500/5" 
                        : "border-gray-200 dark:border-neutral-800"
                    )}
                  >
                    <div className={cn(
                      "w-12 h-12 rounded-full flex items-center justify-center shrink-0 shadow-sm",
                      getTypeStyles(notification.type)
                    )}>
                      {getIcon(notification.type)}
                    </div>

                    <div className="flex-1 space-y-2">
                      <div className="flex items-start justify-between">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <h3 className={cn(
                              "font-bold text-lg leading-tight",
                              !notification.isRead ? "text-gray-900 dark:text-white" : "text-gray-500 dark:text-gray-400"
                            )}>
                              {notification.title}
                            </h3>
                            {!notification.isRead && (
                              <Badge className="bg-teal-500 text-white border-none text-[10px] h-4 px-1.5">NOVO</Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed max-w-2xl">
                            {notification.description}
                          </p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-xs font-semibold text-gray-400">
                            {format(new Date(notification.createdAt), 'dd/MM/yyyy')}
                          </p>
                          <p className="text-[10px] text-gray-400 mt-1">
                            {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true, locale: ptBR })}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center justify-between pt-2">
                        <div className="flex items-center gap-3">
                          {notification.link && (
                            <Button 
                              variant="link" 
                              size="sm" 
                              onClick={() => navigate(notification.link!)}
                              className="p-0 h-auto text-teal-600 dark:text-teal-400 font-bold flex items-center gap-1 text-xs"
                            >
                              Ver Detalhes <ArrowRight className="w-3 h-3" />
                            </Button>
                          )}
                          {!notification.isRead && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => markAsRead(notification.id)}
                              className="h-auto py-1 px-2 text-gray-500 hover:text-teal-600 text-[11px]"
                            >
                              Marcar como lida
                            </Button>
                          )}
                        </div>
                        
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          onClick={() => deleteNotification(notification.id)}
                          className="h-8 w-8 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="flex flex-col items-center justify-center py-20 bg-white dark:bg-neutral-900 rounded-2xl border border-dashed border-gray-200 dark:border-neutral-800 text-center">
                  <div className="w-20 h-20 bg-gray-50 dark:bg-neutral-800 rounded-full flex items-center justify-center mb-6">
                    <Bell className="w-10 h-10 text-gray-200 dark:text-neutral-700" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">Nenhuma notificação encontrada</h3>
                  <p className="text-gray-500 mt-2 max-w-xs mx-auto">
                    Não encontramos notificações que correspondam ao filtro selecionado.
                  </p>
                  <Button 
                    variant="outline" 
                    onClick={() => setFilter('all')}
                    className="mt-6"
                  >
                    Limpar Filtros
                  </Button>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
